import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import './index.scss';

const limitToCodePoints = (value, maxLength) => {
  if (!Number.isInteger(maxLength) || maxLength < 0) {
    return value;
  }
  return Array.from(value || '').slice(0, maxLength).join('');
};

const resolveNativeMaxLength = (maxLength, nativeMaxLength) => (
  nativeMaxLength !== false && Number.isInteger(maxLength) && maxLength >= 0
    ? maxLength
    : undefined
);

const normalizeSavedValue = (value) => (value || '').trim();

/**
 * Inline single-line text editor. When `editing` is false, renders the
 * displayed value (or `placeholder` when empty). When `editing` is true,
 * renders an editorial-style input that auto-saves on blur via `onSave`.
 * The wrapper component keeps the input's draft state locally and calls
 * onSave only when the value actually changed.
 */
export default function EditableText({
  value = '',
  editing,
  placeholder = '',
  emptyLabel = 'Not set',
  ariaLabel,
  onSave,
  className = '',
  multiline = false,
  autoResize = false,
  rows = 3,
  maxLength,
  nativeMaxLength = true,
  inputClassName = '',
  displayClassName = '',
  inputMode,
  pattern,
  onSaveError,
}) {
  const [draft, setDraft] = useState(value || '');
  const draftRef = useRef(value || '');
  const inputRef = useRef(null);
  const lastSavedRef = useRef(value || '');
  const saveRequestRef = useRef(0);
  const lastSuccessfulSaveRequestRef = useRef(0);
  const rollbackRequestRef = useRef(0);
  const rollbackDraftValueRef = useRef(null);
  const suppressBlurSaveRef = useRef(false);

  const setDraftValue = useCallback((nextDraft) => {
    const resolvedDraft = nextDraft || '';
    draftRef.current = resolvedDraft;
    setDraft(resolvedDraft);
  }, []);

  const clearRollbackState = useCallback(() => {
    rollbackRequestRef.current = 0;
    rollbackDraftValueRef.current = null;
  }, []);

  useEffect(() => {
    setDraftValue(value || '');
    lastSavedRef.current = value || '';
    lastSuccessfulSaveRequestRef.current = saveRequestRef.current;
    clearRollbackState();
  }, [clearRollbackState, setDraftValue, value]);

  const handleBlur = useCallback(() => {
    if (suppressBlurSaveRef.current) {
      suppressBlurSaveRef.current = false;
      return;
    }
    const next = normalizeSavedValue(draftRef.current);
    if (next === normalizeSavedValue(lastSavedRef.current)) {
      return;
    }
    if (typeof onSave === 'function') {
      const requestId = saveRequestRef.current + 1;
      saveRequestRef.current = requestId;
      const handleSaveFailure = (err) => {
        if (saveRequestRef.current !== requestId) {
          return;
        }
        const rollbackValue = lastSavedRef.current || '';
        if (normalizeSavedValue(draftRef.current) === next) {
          rollbackRequestRef.current = requestId;
          rollbackDraftValueRef.current = rollbackValue;
          setDraftValue(rollbackValue);
        }
        onSaveError?.(err, { previousValue: rollbackValue, attemptedValue: next });
        console.error('EditableText save failed', err);
      };
      try {
        Promise.resolve(onSave(next))
          .then(() => {
            if (requestId > lastSuccessfulSaveRequestRef.current) {
              lastSuccessfulSaveRequestRef.current = requestId;
              lastSavedRef.current = next;
              if (
                rollbackRequestRef.current > requestId
                && normalizeSavedValue(draftRef.current) === normalizeSavedValue(rollbackDraftValueRef.current)
              ) {
                rollbackDraftValueRef.current = next;
                setDraftValue(next);
              } else if (rollbackRequestRef.current <= requestId) {
                clearRollbackState();
              }
            }
          })
          .catch(handleSaveFailure);
      } catch (err) {
        handleSaveFailure(err);
      }
      return;
    }
    lastSavedRef.current = next;
  }, [clearRollbackState, onSave, onSaveError, setDraftValue]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      suppressBlurSaveRef.current = true;
      setDraftValue(lastSavedRef.current || '');
      e.currentTarget.blur();
    } else if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, [multiline, setDraftValue]);

  const resizeToContent = useCallback(() => {
    const el = inputRef.current;
    if (!el || !autoResize || !multiline) return;
    // Collapse first so the height shrinks when text is removed, then grow to
    // fit the full content. With `box-sizing: border-box` the set height
    // includes the borders, which `scrollHeight` excludes — add them back so
    // the content isn't clipped by a couple of pixels.
    el.style.height = 'auto';
    const borders = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + borders}px`;
  }, [autoResize, multiline]);

  const handleChange = useCallback((e) => {
    const nextDraft = limitToCodePoints(e.target.value, maxLength);
    setDraftValue(nextDraft);
    if (
      rollbackRequestRef.current > 0
      && normalizeSavedValue(nextDraft) !== normalizeSavedValue(rollbackDraftValueRef.current)
    ) {
      clearRollbackState();
    }
  }, [clearRollbackState, maxLength, setDraftValue]);

  // Keep the textarea tall enough to show all its text when auto-resizing.
  // Runs on mount and whenever the draft changes (typing, external value sync).
  useLayoutEffect(() => {
    resizeToContent();
  }, [draft, resizeToContent, editing]);

  const classes = ['editable-text', editing ? 'editable-text--editing' : '', className].filter(Boolean).join(' ');
  const inputMaxLength = resolveNativeMaxLength(maxLength, nativeMaxLength);

  if (!editing) {
    const displayValue = value && String(value).trim().length > 0 ? value : (
      <span className="editable-text__empty">{emptyLabel}</span>
    );
    return (
      <span className={`${classes} ${displayClassName}`.trim()}>{displayValue}</span>
    );
  }

  if (multiline) {
    return (
      <textarea
        ref={inputRef}
        className={`editable-text__textarea${autoResize ? ' editable-text__textarea--auto-resize' : ''} ${inputClassName}`}
        value={draft}
        rows={rows}
        placeholder={placeholder}
        aria-label={ariaLabel}
        maxLength={inputMaxLength}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      className={`editable-text__input ${inputClassName}`}
      value={draft}
      placeholder={placeholder}
      aria-label={ariaLabel}
      maxLength={inputMaxLength}
      inputMode={inputMode}
      pattern={pattern}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
}

EditableText.propTypes = {
  value: PropTypes.string,
  editing: PropTypes.bool,
  placeholder: PropTypes.string,
  emptyLabel: PropTypes.node,
  ariaLabel: PropTypes.string,
  onSave: PropTypes.func,
  className: PropTypes.string,
  multiline: PropTypes.bool,
  autoResize: PropTypes.bool,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
  nativeMaxLength: PropTypes.bool,
  inputClassName: PropTypes.string,
  displayClassName: PropTypes.string,
  inputMode: PropTypes.string,
  pattern: PropTypes.string,
  onSaveError: PropTypes.func,
};
